import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBRL(value: number | string | null | undefined): string {
  const n = typeof value === "string" ? Number(value) : (value ?? 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export function nomeMes(mes: number): string {
  return MESES[mes - 1] ?? "";
}

export function formatarData(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const DIAS_SEMANA_LONGO = [
  "Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira",
  "Quinta-feira", "Sexta-feira", "Sábado",
];

export function diaDaSemana(iso: string, longo = false): string {
  const dow = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return (longo ? DIAS_SEMANA_LONGO : DIAS_SEMANA)[dow]!;
}

type Bloco = "manha" | "tarde" | "noite" | "pernoite";

const SIGLA: Record<Bloco, string> = {
  manha: "M",
  tarde: "T",
  noite: "N",
  pernoite: "P",
};

const ORDEM_CRONOLOGICA: Record<Bloco, number> = {
  manha: 0,
  tarde: 1,
  noite: 2,
  pernoite: 3,
};

const COR_BLOCO: Record<Bloco, string> = {
  manha: "bg-amber-100 text-amber-800 ring-amber-200",
  tarde: "bg-sky-100 text-sky-800 ring-sky-200",
  noite: "bg-indigo-100 text-indigo-800 ring-indigo-200",
  pernoite: "bg-slate-200 text-slate-700 ring-slate-300",
};

function toMin(h: string): number {
  const parts = h.split(":");
  const hh = Number(parts[0] ?? 0);
  const mm = Number(parts[1] ?? 0);
  return hh * 60 + mm;
}

/**
 * Calcula a label de período (M, T, N, P, M+T, T+N, N+P) para um turno
 * baseado nas horas de início e fim. Considera turnos que cruzam meia-noite.
 *
 * Blocos:
 *   manhã    06:00–12:00
 *   tarde    12:00–18:00
 *   noite    18:00–24:00
 *   pernoite 00:00–06:00
 *
 * Retorna até 2 blocos: principal + secundário se este cobrir >= 30% do tempo.
 */
export function periodoDoTurno(
  horaInicio: string,
  horaFim: string,
): { label: string; classe: string } {
  let inicio = toMin(horaInicio);
  let fim = toMin(horaFim);

  const blocos: Record<Bloco, number> = { manha: 0, tarde: 0, noite: 0, pernoite: 0 };

  function adicionar(de: number, ate: number) {
    if (ate <= de) return;
    blocos.pernoite += Math.max(0, Math.min(ate, 360) - Math.max(de, 0));
    blocos.manha += Math.max(0, Math.min(ate, 720) - Math.max(de, 360));
    blocos.tarde += Math.max(0, Math.min(ate, 1080) - Math.max(de, 720));
    blocos.noite += Math.max(0, Math.min(ate, 1440) - Math.max(de, 1080));
  }

  if (fim === 0) fim = 1440;
  if (fim > inicio) {
    adicionar(inicio, fim);
  } else {
    adicionar(inicio, 1440);
    adicionar(0, fim);
  }

  const ordem = (Object.entries(blocos) as Array<[Bloco, number]>)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  if (ordem.length === 0) return { label: "—", classe: COR_BLOCO.manha };

  const total = ordem.reduce((s, [, v]) => s + v, 0);
  const principal = ordem[0]![0];
  const secEntry = ordem[1];
  const secundario = secEntry && secEntry[1] / total >= 0.3 ? secEntry[0] : null;

  // Ordena as siglas cronologicamente (M < T < N < P) para evitar
  // labels invertidos como "N+T" para turnos 13:00-00:00, que devem ser "T+N".
  let label: string;
  if (secundario) {
    const [primeiro, segundo] =
      ORDEM_CRONOLOGICA[principal] <= ORDEM_CRONOLOGICA[secundario]
        ? [principal, secundario]
        : [secundario, principal];
    label = `${SIGLA[primeiro]}+${SIGLA[segundo]}`;
  } else {
    label = SIGLA[principal];
  }
  return { label, classe: COR_BLOCO[principal] };
}

type EstiloEvento = {
  label: string;
  pillClasse: string;
  celulaClasse: string;
  numeroClasse: string;
};

const ESTILO_EVENTO: Record<string, EstiloEvento> = {
  feriado_nacional: {
    label: "Feriado nacional",
    pillClasse: "bg-rose-100 text-rose-800 ring-rose-200",
    celulaClasse: "ring-1 ring-rose-200 bg-rose-50/40",
    numeroClasse: "text-rose-700",
  },
  feriado_estadual: {
    label: "Feriado estadual",
    pillClasse: "bg-orange-100 text-orange-800 ring-orange-200",
    celulaClasse: "ring-1 ring-orange-200 bg-orange-50/40",
    numeroClasse: "text-orange-700",
  },
  feriado_municipal: {
    label: "Feriado municipal",
    pillClasse: "bg-amber-100 text-amber-800 ring-amber-200",
    celulaClasse: "ring-1 ring-amber-200 bg-amber-50/40",
    numeroClasse: "text-amber-700",
  },
  ponto_facultativo: {
    label: "Ponto facultativo",
    pillClasse: "bg-violet-100 text-violet-800 ring-violet-200",
    celulaClasse: "ring-1 ring-violet-200 bg-violet-50/40",
    numeroClasse: "text-violet-700",
  },
  aviso: {
    label: "Aviso",
    pillClasse: "bg-sky-100 text-sky-800 ring-sky-200",
    celulaClasse: "ring-1 ring-sky-200 bg-sky-50/30",
    numeroClasse: "text-sky-700",
  },
  aniversario: {
    label: "Aniversário",
    pillClasse: "bg-yellow-100 text-yellow-800 ring-yellow-300",
    celulaClasse: "ring-1 ring-yellow-300 bg-yellow-50/50",
    numeroClasse: "text-yellow-700",
  },
};

export function estiloEvento(tipo: string): EstiloEvento {
  return ESTILO_EVENTO[tipo] ?? ESTILO_EVENTO.aviso!;
}

export function rotuloTipoEvento(tipo: string): string {
  return estiloEvento(tipo).label;
}

// ── F2 (07/09): status real do turno ──
// 'agendado', 'pendente' e 'aprovado' sao o MESMO recado na tela: ninguem
// conferiu ainda. Por isso devolvem null e nao viram badge — badge so
// aparece quando alguem decidiu alguma coisa sobre o turno.
export type EstiloStatusTurno = { sigla: string; rotulo: string; classe: string };

const STATUS_TURNO: Record<string, EstiloStatusTurno> = {
  realizado: { sigla: "R", rotulo: "Realizado", classe: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
  cancelado: { sigla: "C", rotulo: "Cancelado", classe: "bg-slate-200 text-slate-600 ring-slate-300" },
  falta: { sigla: "F", rotulo: "Falta", classe: "bg-red-100 text-red-800 ring-red-200" },
};

export function estiloStatusTurno(status: string): EstiloStatusTurno | null {
  return STATUS_TURNO[status] ?? null;
}
